/**
 * CompanyForm 컴포넌트 - 회사 등록/수정 폼
 */

import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';
import { REGIMEN_FISCAL_OPTIONS, USO_CFDI_OPTIONS, PLACEHOLDERS } from './constants';
import type { CompanyFormData, CompanyTranslations } from './types';

interface CompanyFormProps {
  formData: CompanyFormData;
  onFormChange: (data: CompanyFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing: boolean;
  translations: CompanyTranslations;
  isSaving?: boolean;
}

export function CompanyForm({
  formData,
  onFormChange,
  onSubmit,
  onCancel,
  isEditing,
  translations: t,
  isSaving = false,
}: CompanyFormProps) {
  const { language } = useLanguage();
  const updateField = (field: keyof CompanyFormData, value: string) => {
    onFormChange({ ...formData, [field]: value });
  };

  return (
    <Card className="p-3 bg-background rounded-[5px] border border-border">
      <div className="mb-3">
        <h3 className="text-[15px] font-semibold text-foreground">
          {isEditing ? t.editCompany : t.addNewCompany}
        </h3>
      </div>

      <div className="space-y-2.5">
        {/* 회사명 및 RFC */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
          <div>
            <Label className="text-foreground text-[13px] font-medium mb-1 block">
              {t.companyName} <span className="text-destructive">*</span>
            </Label>
            <Input
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary"
              placeholder="OLULO Technologies Inc."
            />
          </div>

          <div>
            <Label className="text-foreground text-[13px] font-medium mb-1 block">
              {t.rfc} <span className="text-destructive">*</span>
            </Label>
            <Input
              value={formData.rfc}
              onChange={(e) => updateField('rfc', e.target.value)}
              className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary"
              placeholder={t.rfcPlaceholder}
            />
          </div>

          <div>
            <Label className="text-foreground text-[13px] font-medium mb-1 block">
              {t.city}
            </Label>
            <Input
              value={formData.city}
              onChange={(e) => updateField('city', e.target.value)}
              className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary"
              placeholder={PLACEHOLDERS.city[language]}
            />
          </div>

          <div>
            <Label className="text-foreground text-[13px] font-medium mb-1 block">
              {t.phone}
            </Label>
            <Input
              value={formData.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary"
              placeholder={t.phonePlaceholder}
            />
          </div>
        </div>

        {/* 주소 */}
        <div>
          <Label className="text-foreground text-[13px] font-medium mb-1 block">
            {t.address} <span className="text-destructive">*</span>
          </Label>
          <Input
            value={formData.address}
            onChange={(e) => updateField('address', e.target.value)}
            className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary"
            placeholder={PLACEHOLDERS.address[language]}
          />
        </div>

        {/* 이메일 */}
        <div>
          <Label className="text-foreground text-[13px] font-medium mb-1 block">
            {t.email}
          </Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary"
            placeholder={t.emailPlaceholder}
          />
        </div>

        {/* CFDI 필수 필드 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
          <div>
            <Label className="text-foreground text-[13px] font-medium mb-1 block">
              {t.taxRegime} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.taxRegime} onValueChange={(value) => updateField('taxRegime', value)}>
              <SelectTrigger className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary">
                <SelectValue placeholder={t.selectRegimen} />
              </SelectTrigger>
              <SelectContent>
                {REGIMEN_FISCAL_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-foreground text-[13px] font-medium mb-1 block">
              {t.cfdiUse} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.cfdiUse} onValueChange={(value) => updateField('cfdiUse', value)}>
              <SelectTrigger className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary">
                <SelectValue placeholder={t.selectUso} />
              </SelectTrigger>
              <SelectContent>
                {USO_CFDI_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-foreground text-[13px] font-medium mb-1 block">
              {t.postalCode} <span className="text-destructive">*</span>
            </Label>
            <Input
              value={formData.postalCode}
              onChange={(e) => updateField('postalCode', e.target.value)}
              className="h-10 bg-muted border-0 rounded-[5px] text-[14px] focus:ring-2 focus:ring-secondary"
              placeholder={t.postalCodePlaceholder}
              maxLength={5}
            />
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            onClick={onCancel}
            variant="outline"
            disabled={isSaving}
            className="flex-1 h-10 border border-border text-muted-foreground hover:bg-muted rounded-[5px] text-[14px]"
          >
            {t.cancel}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSaving}
            className="flex-1 h-10 bg-secondary hover:bg-secondary/90 text-white rounded-[5px] text-[14px]"
          >
            {isSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {t.register}
          </Button>
        </div>
      </div>
    </Card>
  );
}
